import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, PlayCircle, Siren } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");

export function AttendancePanel({ canRun }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      setData((await api.get("/attendance/overview")).data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function runNow() {
    setRunning(true);
    setNotice("");
    try {
      const result = (await api.post("/attendance/shift-check/run")).data;
      setNotice(result.status === "outside window" ? "Outside the check window (weekdays 6:00 a.m.–6:00 p.m. ET); nothing to evaluate." : `Checked ${result.checked} scheduled shifts: ${result.ok} clocked in, ${result.waiting} within 30 minutes, ${result.tasks_created} new tasks, ${result.ceo_notices} CEO notices.`);
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setRunning(false);
    }
  }

  if (error && !data) return <p className="form-message error-message" data-testid="attendance-error">{error}</p>;
  if (!data) return <p className="projection-loading">Loading attendance…</p>;
  const latest = data.checks[0];
  return (
    <section className="connecteam-panel" data-testid="connecteam-attendance">
      <div className="section-heading">
        <div>
          <h2><Siren size={16} /> Attendance escalation</h2>
          <p>{data.window}. No clock-in 30 minutes after a scheduled start opens a task for {data.owner || "the Staffing Coordinator"}{data.backup ? ` (backup ${data.backup})` : ""}; still open at 45 minutes, the CEO is emailed once.</p>
        </div>
        {canRun && <button type="button" className="button button-secondary" data-testid="attendance-run-button" disabled={running} onClick={runNow}><PlayCircle size={14} /> {running ? "Checking…" : "Run check now"}</button>}
      </div>
      {error && <p className="form-message error-message" data-testid="attendance-error">{error}</p>}
      {notice && <p className="form-message success-message" data-testid="attendance-notice">{notice}</p>}
      <p className="pairing-meta" data-testid="attendance-last-check">
        {latest ? `Last check ${fmtTime(latest.ran_at)} · ${latest.status}${latest.window ? ` · ${latest.checked} shifts checked, ${latest.tasks_created} tasks, ${latest.ceo_notices} CEO notices` : ""}` : "No check has run yet."}
        {latest?.skipped && Object.keys(latest.skipped).length > 0 && ` · skipped: ${Object.entries(latest.skipped).map(([reason, count]) => `${count} ${reason}`).join(", ")}`}
      </p>
      <div className="requests-table-panel">
        <table className="requests-table connecteam-table" data-testid="attendance-table">
          <thead><tr><th>Date</th><th>Person</th><th>Site</th><th>Shift</th><th>Task</th><th>CEO notice</th><th>Resolution</th></tr></thead>
          <tbody>
            {data.escalations.map((row) => (
              <tr key={row.id} data-testid={`attendance-row-${row.id}`}>
                <td>{row.date}</td>
                <td><strong>{row.person_name}</strong></td>
                <td>{row.site}</td>
                <td>{row.shift_label} · {row.scheduled_hours} h</td>
                <td><span className={`status-chip ${row.task_status === "Verified" || row.task_status === "Done – Pending Verification" ? "status-active" : "status-pending"}`} data-testid={`attendance-task-status-${row.id}`}>{row.task_status || "—"}</span><br /><small className="pairing-meta">{row.owner_name}</small></td>
                <td data-testid={`attendance-ceo-${row.id}`}>{row.ceo_notified_at ? fmtTime(row.ceo_notified_at) : <span className="pairing-meta">not sent</span>}</td>
                <td data-testid={`attendance-resolution-${row.id}`}>{row.resolution ? data.close_reasons[row.resolution] || row.resolution : <span className="pairing-meta">open</span>}{row.resolution_note && <><br /><small>{row.resolution_note}</small></>}</td>
              </tr>
            ))}
            {!data.escalations.length && <tr><td colSpan={7} className="provider-empty-state" data-testid="attendance-empty"><AlertTriangle size={13} /> No clock-in escalations in the past 7 days.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
